import React, { useCallback, useState } from 'react';
import { Stack, Text, Textarea } from '@mantine/core';
import { ConfirmModal } from '../common';
import { useCancelSurvey } from '../../services/survey';
import { showSuccessNotification } from '../../notifications';
import { getErrorMessage } from '../../helpers/getErrorMessage';

type CancelSurveyConfirmModalProps = {
    opened: boolean;
    onClose: () => void;
    surveyId: string | null;
    onSuccess?: () => void | Promise<void>;
};

export function CancelSurveyConfirmModal({
    opened,
    onClose,
    surveyId,
    onSuccess,
}: CancelSurveyConfirmModalProps) {
    const [cancelReason, setCancelReason] = useState('');
    const [cancelSurvey, { loading: isCancellingSurvey }] = useCancelSurvey();
    const trimmedCancelReason = cancelReason.trim();

    const handleClose = useCallback(() => {
        onClose();
        setCancelReason('');
    }, [onClose]);

    const handleConfirm = useCallback(async () => {
        if (!surveyId) {
            throw new Error('Survey ID is missing');
        }

        if (!trimmedCancelReason) {
            throw new Error('Please provide a cancellation reason');
        }

        try {
            await cancelSurvey({
                variables: {
                    input: {
                        surveyId,
                        reason: trimmedCancelReason,
                    },
                },
            });
            await onSuccess?.();
            setCancelReason('');
            showSuccessNotification('Survey cancelled successfully');
        } catch (error: unknown) {
            const message = getErrorMessage(error);
            console.error('Survey cancellation failed', { surveyId, error });
            throw new Error(message);
        }
    }, [surveyId, trimmedCancelReason, cancelSurvey, onSuccess]);

    return (
        <ConfirmModal
            opened={opened}
            onClose={handleClose}
            title="Are you sure?"
            confirmLabel="Cancel Survey"
            cancelLabel="Keep Survey"
            onConfirm={handleConfirm}
            confirmColor="red"
            loading={isCancellingSurvey}
            confirmButtonProps={{ disabled: !trimmedCancelReason }}
        >
            <Stack gap="sm">
                <Text size="sm" c="dark.7">
                    Are you sure you want to cancel this survey? This will move
                    it to history and cannot be undone.
                </Text>
                <Textarea
                    label="Cancellation reason"
                    placeholder="Enter the reason for cancelling this survey"
                    value={cancelReason}
                    onChange={event =>
                        setCancelReason(event.currentTarget.value)
                    }
                    minRows={3}
                    maxLength={1000}
                    required
                    withAsterisk
                />
            </Stack>
        </ConfirmModal>
    );
}
